// src/components/common/CustomSliderRow.js
// Filepath: // src/components/common/CustomSliderRow.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import Poster from "./Poster";

/**
 * Carousel row with locked anchor, left peek, and a fixed-height title band.
 * Emits onFocusItem(item, { rowIndex, colIndex }).
 */
export default function CustomSliderRow({
  title,
  items = [],
  rowIndex = 0,
  onEnterItem,
  onFocusItem,

  // layout
  edgePeekPx = 60,
  gapPx = 16,
  cardWidth = 372,
  cardHeight = 210,

  // header band controls (must match HomeScene.TITLE_H)
  titleHeight = 48,
  titlePaddingBottom = 8,
  titleVariant = "h2",
  titleColor = "text.primary",

  // fades
  fadeLeftOpacity = 0.25,
  fadeRightOpacity = 0.85,

  // new - seed from remembered memory
  initialLeadIndex = 0,
}) {
  const [leadIndex, setLeadIndex] = useState(
    Math.max(0, Math.min(initialLeadIndex ?? 0, Math.max(0, items.length - 1)))
  );

  // keep leadIndex valid if items length changes
  useEffect(() => {
    setLeadIndex((i) => Math.max(0, Math.min(i, Math.max(0, items.length - 1))));
  }, [items.length]);

  // react to updates of initial seed from parent memory
  useEffect(() => {
    const next = Math.max(0, Math.min(initialLeadIndex ?? 0, Math.max(0, items.length - 1)));
    setLeadIndex(next);
  }, [initialLeadIndex, items.length]);

  const stride = cardWidth + gapPx;
  const padLeft = leadIndex > 0 ? edgePeekPx : 0;
  const stripTranslate = useMemo(() => `translateX(-${leadIndex * stride}px)`, [leadIndex, stride]);
  const stripMarginLeft = leadIndex > 0 ? -edgePeekPx : 0;

  const handleFocusTile = useCallback(
    (colIndex, item) => {
      setLeadIndex(colIndex);
      onFocusItem?.(item, { rowIndex, colIndex });
    },
    [onFocusItem, rowIndex]
  );

  return (
    <Box className="custom-rail" sx={{ mt: 0 }}>
      <Box
        sx={{
          height: `${titleHeight}px`,
          boxSizing: "border-box",
          display: "flex",
          alignItems: "flex-end",
          pb: `${titlePaddingBottom}px`,
          overflow: "hidden",
        }}
      >
        {!!title && (
          <Typography
            component={titleVariant}
            variant={titleVariant}
            sx={{
              color: titleColor,
              m: 0,
              lineHeight: 1.1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title}
          </Typography>
        )}
      </Box>

      <Box className="custom-rail-viewport" sx={{ overflow: "hidden", position: "relative", pl: `${padLeft}px` }}>
        <Box
          className="custom-rail-strip"
          sx={{
            display: "flex",
            gap: `${gapPx}px`,
            transform: stripTranslate,
            marginLeft: `${stripMarginLeft}px`,
            willChange: "transform",
            transition: "transform 320ms ease",
          }}
        >
          {items.map((it, i) => {
            const distance = i - leadIndex;
            const opacity = distance === 0 ? 1 : distance < 0 ? fadeLeftOpacity : fadeRightOpacity;
            return (
              <Box key={it.id ?? i} sx={{ opacity, transition: "opacity 80ms ease" }}>
                <Poster
                  item={it}
                  rowIndex={rowIndex}
                  index={i}
                  width={cardWidth}
                  height={cardHeight}
                  onEnter={onEnterItem}
                  onFocus={() => handleFocusTile(i, it)}
                  overlayTitle
                  overlayTitleSize={18}
                />
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
